import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowRight, Check, Eye, EyeOff, Link2Off } from 'lucide-react';

/*
 * The second sitting — everything we did not ask before the call was booked.
 *
 * Reached only from an emailed link, which is also what proves the address. Three
 * properties this page has to keep:
 *
 *   RESUMABLE   Answers save as they advance, not at the end. Somebody who fills half of
 *               this on a phone, closes it, and opens the same link three days later gets
 *               their answers back. That is the whole reason it is a link and not a form.
 *
 *   HONEST      Nothing here is a gate. The call is already booked; these questions decide
 *               what we build for it. Saying that plainly is what makes them worth
 *               answering, and it happens to be true.
 *
 *   TERMINAL    It ends in a password and a session, so the applicant lands in the portal
 *               rather than being told to go and log in.
 *
 * The book questions are `11-APPLICATION.md` §8.2's five factors minus client_count, which
 * was asked at submit. Values match backend/scorecard.py — the form and the scorecard are
 * one artefact in two places.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEAM_SIZES = [
  { value: '1-4', label: '1 to 4' },
  { value: '5-20', label: '5 to 20' },
  { value: '21-50', label: '21 to 50' },
  { value: '51-200', label: '51 to 200' },
  { value: '200+', label: 'More than 200' },
];

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Chandigarh',
];

const VERTICALS = [
  'Education', 'Real estate', 'Healthcare', 'Retail & D2C',
  'Fitness & wellness', 'Travel & hospitality', 'Financial services', 'Other',
];

const BOOK_QUESTIONS = [
  {
    field: 'retainer_share',
    label: 'How many of your clients are on a monthly retainer?',
    help: 'Recurring work is what makes a platform underneath it worth running.',
    options: [
      { value: 'gt75', label: 'More than 75%' },
      { value: '50-75', label: '50 to 75%' },
      { value: '25-49', label: '25 to 49%' },
      { value: 'lt25', label: 'Under 25%, mostly project work' },
    ],
  },
  {
    field: 'delivery_capability',
    label: 'Who operates client software today?',
    help: 'We are asking whether there is somebody who could run Expedify for a client, not whether they have used ours.',
    options: [
      { value: 'platform_team', label: 'A dedicated ops team already runs a CRM or automation platform for clients' },
      { value: 'campaign_ops', label: 'We run campaigns and ad ops, but no platform of our own' },
      { value: 'founder_led', label: 'Founder-operated, with some tooling' },
      { value: 'outsourced', label: 'We outsource all delivery' },
    ],
  },
  // No `vertical_fit` question. It asked how much of the book is education, real estate or
  // healthcare, which the industries picker in step 1 already answers more precisely — and
  // that picker is what actually decides the demo we build.
  {
    field: 'commercial_posture',
    label: 'How does software reach your clients today?',
    help: 'This decides which arrangement we open with: commission, or buying credits and billing your clients yourself.',
    options: [
      { value: 'resells', label: 'We resell or mark up licences' },
      { value: 'bundles', label: 'We bundle tools into the retainer' },
      { value: 'client_direct', label: 'The client buys direct and we operate it' },
      { value: 'hours_only', label: 'We bill hours only' },
    ],
  },
];

const STEPS = ['Your agency', 'Your book', 'Your login'];

const ChoiceQuestion = ({ question, value, onChange }) => (
  <fieldset>
    <legend className="text-sm font-medium text-[#1A1A1A]">{question.label}</legend>
    {question.help && <p className="mt-1 text-xs leading-relaxed text-[#64748B]">{question.help}</p>}
    <RadioGroup value={value || ''} onValueChange={onChange} className="mt-3 gap-2" data-testid={`question-${question.field}`}>
      {question.options.map((o) => {
        const id = `${question.field}-${o.value}`;
        const selected = value === o.value;
        return (
          <div
            key={o.value}
            className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${
              selected ? 'border-[#ee785b] bg-[#ee785b]/[0.06]' : 'border-[#E2E2DF] bg-white hover:border-[#cbd5e1]'
            }`}
          >
            <RadioGroupItem value={o.value} id={id} className="mt-0.5 border-[#94A3B8] text-[#ee785b]" />
            <Label htmlFor={id} className="cursor-pointer text-sm font-normal leading-relaxed text-[#1A1A1A]">
              {o.label}
            </Label>
          </div>
        );
      })}
    </RadioGroup>
  </fieldset>
);

const ContinuePage = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const { adoptSession } = useAuth();

  const [state, setState] = useState('loading'); // loading · ready · dead
  const [needsPassword, setNeedsPassword] = useState(true);
  const [applicant, setApplicant] = useState({ email: '', full_name: '' });
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) {
      setState('dead');
      return;
    }
    axios
      .get(`${API_URL}/api/auth/resume/${token}`)
      .then(({ data }) => {
        if (!data.valid) {
          setState('dead');
          return;
        }
        setApplicant({ email: data.email, full_name: data.full_name });
        setNeedsPassword(data.needs_password);
        // Whatever they answered last time comes back with them. `?? ''` rather than `||`
        // so a stored `false` on the competitor tick survives the round trip.
        setAnswers(
          Object.fromEntries(Object.entries(data.answers || {}).map(([k, v]) => [k, v ?? ''])),
        );
        setState('ready');
      })
      .catch(() => setState('dead'));
  }, [token]);

  const set = (field, value) => setAnswers((prev) => ({ ...prev, [field]: value }));

  const toggleVertical = (v) =>
    setAnswers((prev) => {
      const current = Array.isArray(prev.verticals) ? prev.verticals : [];
      return {
        ...prev,
        verticals: current.includes(v) ? current.filter((x) => x !== v) : [...current, v],
      };
    });

  /** Persist what has been answered so far. Called on every step change rather than only
   *  at the end, which is what makes closing the tab free. */
  const save = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/resume`, { token, ...answers });
    } catch {
      // Saving is best-effort on the way through: the applicant is mid-form and a failed
      // autosave is not their problem to solve. `complete` sends everything again.
    }
  }, [token, answers]);

  const next = async () => {
    await save();
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const finish = async (e) => {
    e.preventDefault();
    if (needsPassword && password.length < 8) {
      toast.error('Please choose a password of at least 8 characters.');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/auth/resume`, { token, ...answers });
      const { data } = await axios.post(`${API_URL}/api/auth/resume/complete`, { token, password });
      adoptSession(data);
      toast.success('Application complete. Welcome to Expedify Partners.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (state === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAFAF8]">
        <span className="h-6 w-6 animate-spin rounded-full border-b-2 border-[#ef785b]" />
      </div>
    );
  }

  if (state === 'dead') {
    return (
      <div className="min-h-screen bg-[#FAFAF8] px-4 py-16">
        <div className="mx-auto max-w-lg rounded-2xl border border-[#E2E2DF] bg-white p-8 shadow-sm">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#ee785b]/10">
            <Link2Off className="h-5 w-5 text-[#ee785b]" />
          </span>
          <h1 className="mt-5 text-xl font-bold text-[#1A1A1A]">This link has expired</h1>
          <p className="mt-3 text-sm leading-relaxed text-[#64748B]">
            Resume links last 30 days, and they stop working once the application is finished. If
            yours is done, sign in. If it is not, write to us and we will send a fresh one.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/login"
              className="inline-flex h-11 items-center justify-center rounded-lg bg-[#ef785b] px-5 text-sm font-semibold text-white hover:bg-[#D44017]"
            >
              Sign in
            </Link>
            <a
              href="mailto:partners@expedify.ai?subject=A%20fresh%20link%20to%20finish%20my%20application"
              className="inline-flex h-11 items-center justify-center rounded-lg border border-[#E2E2DF] px-5 text-sm font-semibold text-[#1A1A1A] hover:border-[#cbd5e1]"
            >
              Ask for a new link
            </a>
          </div>
        </div>
      </div>
    );
  }

  const firstName = (applicant.full_name || '').split(' ')[0];

  return (
    <div className="min-h-screen bg-[#FAFAF8] px-4 py-8">
      <div className="mx-auto max-w-2xl">
        <div className="rounded-2xl border border-[#E2E2DF] bg-white p-8 shadow-sm">
          <img src="/light_logo.webp" alt="Expedify Logo" className="mb-6 h-10 w-auto" />

          <h1 className="mb-2 text-2xl font-bold text-[#1A1A1A]">
            {firstName ? `Nearly there, ${firstName}` : 'Nearly there'}
          </h1>
          <p className="mb-8 text-[#64748B]">
            Your call is booked. These last questions decide the demo we build for it, so we are
            showing you your own business rather than our feature list. About two minutes, and you
            can stop and come back to this link any time.
          </p>

          <ol className="mb-8 flex items-center gap-2" aria-label="Progress">
            {STEPS.map((label, i) => {
              const done = i < step;
              const current = i === step;
              return (
                <li key={label} className="flex flex-1 items-center gap-2">
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                      done
                        ? 'bg-[#ee785b] text-white'
                        : current
                          ? 'border-2 border-[#ee785b] text-[#ee785b]'
                          : 'border border-[#E2E2DF] text-[#94A3B8]'
                    }`}
                    aria-current={current ? 'step' : undefined}
                  >
                    {done ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : i + 1}
                  </span>
                  <span className={`hidden text-sm sm:inline ${current ? 'font-semibold text-[#1A1A1A]' : 'text-[#64748B]'}`}>
                    {label}
                  </span>
                  {i < STEPS.length - 1 && <span className="h-px flex-1 bg-[#E2E2DF]" aria-hidden />}
                </li>
              );
            })}
          </ol>

          <form onSubmit={finish} className="space-y-6">
            {step === 0 && (
              <>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="company_name" className="text-sm font-medium text-[#1A1A1A]">
                      Agency or company name
                    </Label>
                    <Input
                      id="company_name"
                      value={answers.company_name || ''}
                      onChange={(e) => set('company_name', e.target.value)}
                      placeholder="Your agency"
                      className="mt-1.5 h-11 rounded-lg border-[#E2E2DF]"
                      data-testid="continue-company"
                    />
                  </div>
                  <div>
                    <Label htmlFor="website" className="text-sm font-medium text-[#1A1A1A]">Website</Label>
                    <Input
                      id="website"
                      value={answers.website || ''}
                      onChange={(e) => set('website', e.target.value)}
                      placeholder="youragency.com"
                      className="mt-1.5 h-11 rounded-lg border-[#E2E2DF]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="designation" className="text-sm font-medium text-[#1A1A1A]">Your role</Label>
                    <Input
                      id="designation"
                      value={answers.designation || ''}
                      onChange={(e) => set('designation', e.target.value)}
                      placeholder="e.g. Founder, Head of Delivery"
                      className="mt-1.5 h-11 rounded-lg border-[#E2E2DF]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="team_size" className="text-sm font-medium text-[#1A1A1A]">Team size</Label>
                    <Select value={answers.team_size || ''} onValueChange={(v) => set('team_size', v)}>
                      <SelectTrigger className="mt-1.5 h-11 rounded-lg border-[#E2E2DF]" data-testid="continue-team-size">
                        <SelectValue placeholder="How many people" />
                      </SelectTrigger>
                      <SelectContent>
                        {TEAM_SIZES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="city" className="text-sm font-medium text-[#1A1A1A]">City</Label>
                    <Input
                      id="city"
                      value={answers.city || ''}
                      onChange={(e) => set('city', e.target.value)}
                      placeholder="Where you are based"
                      className="mt-1.5 h-11 rounded-lg border-[#E2E2DF]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state" className="text-sm font-medium text-[#1A1A1A]">State</Label>
                    <Select value={answers.state || ''} onValueChange={(v) => set('state', v)}>
                      <SelectTrigger className="mt-1.5 h-11 rounded-lg border-[#E2E2DF]">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {INDIAN_STATES.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <fieldset>
                  <legend className="text-sm font-medium text-[#1A1A1A]">Which industries do your clients sell in?</legend>
                  <p className="mt-1 text-xs text-[#64748B]">Pick any that apply. This is what your demo gets built on.</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {VERTICALS.map((v) => {
                      const on = (answers.verticals || []).includes(v);
                      return (
                        <button
                          key={v}
                          type="button"
                          onClick={() => toggleVertical(v)}
                          aria-pressed={on}
                          className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${
                            on
                              ? 'border-[#ee785b] bg-[#ee785b] text-white'
                              : 'border-[#E2E2DF] bg-white text-[#64748B] hover:border-[#cbd5e1]'
                          }`}
                        >
                          {v}
                        </button>
                      );
                    })}
                  </div>
                </fieldset>

                {/* No "do you build a competing platform?" tick. The application already
                    offers "Our own platform" when it asks what they run client work on, so
                    ticking this was answering the same question a step later. It surfaces
                    on the approvals screen from that answer instead, where a human can ask
                    the follow-up that actually matters: do you resell it. */}
              </>
            )}

            {step === 1 && (
              <>
                <div className="space-y-7">
                  {BOOK_QUESTIONS.map((q) => (
                    <ChoiceQuestion key={q.field} question={q} value={answers[q.field]} onChange={(v) => set(q.field, v)} />
                  ))}
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="referral_source" className="text-sm font-medium text-[#1A1A1A]">
                      How did you hear about us?
                    </Label>
                    <Select value={answers.referral_source || ''} onValueChange={(v) => set('referral_source', v)}>
                      <SelectTrigger className="mt-1.5 h-11 rounded-lg border-[#E2E2DF]">
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="linkedin">LinkedIn</SelectItem>
                        <SelectItem value="email">An email from Expedify</SelectItem>
                        <SelectItem value="call">A call from Expedify</SelectItem>
                        <SelectItem value="google">Google search</SelectItem>
                        <SelectItem value="friend">Another agency or colleague</SelectItem>
                        <SelectItem value="event">Event or webinar</SelectItem>
                        <SelectItem value="customer">We already use Expedify</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="linkedin_url" className="text-sm font-medium text-[#1A1A1A]">LinkedIn profile</Label>
                    <Input
                      id="linkedin_url"
                      value={answers.linkedin_url || ''}
                      onChange={(e) => set('linkedin_url', e.target.value)}
                      placeholder="https://linkedin.com/in/yourprofile"
                      className="mt-1.5 h-11 rounded-lg border-[#E2E2DF]"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="network_description" className="text-sm font-medium text-[#1A1A1A]">
                    Anything else we should know?
                  </Label>
                  <textarea
                    id="network_description"
                    value={answers.network_description || ''}
                    onChange={(e) => set('network_description', e.target.value)}
                    placeholder="Optional. A client you have in mind, or a question you want answered on the call."
                    className="mt-1.5 min-h-[80px] w-full rounded-lg border border-[#E2E2DF] px-3 py-2 text-sm focus:border-[#ef785b] focus:ring-2 focus:ring-[#ef785b]/20"
                  />
                </div>
              </>
            )}

            {step === 2 && (
              <>
                {needsPassword ? (
                  <div>
                    <Label htmlFor="password" className="text-sm font-medium text-[#1A1A1A]">
                      Choose a password <span className="text-red-500">*</span>
                    </Label>
                    <p className="mt-1 text-xs leading-relaxed text-[#64748B]">
                      For <span className="font-medium text-[#1A1A1A]">{applicant.email}</span>. This is
                      the last step, and it opens your partner portal.
                    </p>
                    <div className="relative mt-1.5">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="At least 8 characters"
                        className="h-11 rounded-lg border-[#E2E2DF] pr-10"
                        minLength={8}
                        data-testid="continue-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#1A1A1A]"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-[#E2E2DF] bg-[#FAFAF8] p-4 text-sm leading-relaxed text-[#64748B]">
                    Your account already has a password, so there is nothing left to set. Finish, and
                    we will take you to the portal.
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <Checkbox
                    id="whatsapp"
                    checked={answers.whatsapp_enabled !== false}
                    onCheckedChange={(c) => set('whatsapp_enabled', Boolean(c))}
                  />
                  <Label htmlFor="whatsapp" className="cursor-pointer text-sm text-[#64748B]">
                    Send me updates on WhatsApp
                  </Label>
                </div>
              </>
            )}

            <div className="flex items-center gap-3 pt-2">
              {step > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep((s) => s - 1)}
                  className="h-12 border-[#E2E2DF] px-5"
                >
                  Back
                </Button>
              )}
              {step < STEPS.length - 1 ? (
                <Button
                  type="button"
                  onClick={next}
                  className="btn-press h-12 flex-1 rounded-lg bg-[#ef785b] font-semibold text-white hover:bg-[#D44017]"
                  data-testid="continue-next"
                >
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={saving}
                  className="btn-press h-12 flex-1 rounded-lg bg-[#ef785b] font-semibold text-white hover:bg-[#D44017]"
                  data-testid="continue-submit"
                >
                  {saving ? (
                    <span className="flex items-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
                      Finishing...
                    </span>
                  ) : (
                    'Finish and open my portal'
                  )}
                </Button>
              )}
            </div>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-[#64748B]">
          Your answers save as you go. Close this and come back to the same link whenever you like.
        </p>
      </div>
    </div>
  );
};

export default ContinuePage;
